import { Badge, Button, Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import React from 'react';

export interface TaskButton {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  icon?: React.ReactNode;
}

export interface Task {
  id: string | number;
  name: string;
  status?: MoleculeTaskTableStatus;
  disabled?: boolean;
  primaryActionButton?: TaskButton;
}

export interface MoleculeTaskTableProps {
  tasks: Task[];
  progressText?: string;
}

export enum MoleculeTaskTableStatus {
  COMPLETED = 'Completed',
  IN_PROGRESS = 'In Progress',
  NOT_STARTED = 'Not Started',
}

export const MoleculeTaskTable: React.FC<MoleculeTaskTableProps> = ({
  tasks,
  progressText,
}) => {
  const completedTasks = tasks.filter(
    (task) => task.status === MoleculeTaskTableStatus.COMPLETED
  ).length;
  const notStartedTasks = tasks.filter(
    (task) => task.status === MoleculeTaskTableStatus.NOT_STARTED
  ).length;

  const totalTasks = tasks.length;

  const overallStatus =
    completedTasks === totalTasks
      ? MoleculeTaskTableStatus.COMPLETED
      : notStartedTasks === totalTasks
        ? MoleculeTaskTableStatus.NOT_STARTED
        : MoleculeTaskTableStatus.IN_PROGRESS;

  return (
    <div className="bg-white rounded-lg border border-bg-tertiary">
      {/* Header */}
      <div className="flex justify-between items-center bg-bg-primary-hover py-xl px-2xl rounded-t-lg">
        <span className="text-sm text-gray-500 mr-4">
          {progressText || `${completedTasks} of ${totalTasks} complete`}
        </span>

        <Badge
          size="sm"
          variant={
            overallStatus === MoleculeTaskTableStatus.COMPLETED
              ? 'success'
              : overallStatus === MoleculeTaskTableStatus.NOT_STARTED
                ? 'gray'
                : 'warning'
          }
        >
          {overallStatus}
        </Badge>
      </div>

      {/* Task List */}
      <div className="py-xl px-2xl flex flex-col gap-xl text-sm">
        {tasks.map((task, index) => (
          <div
            key={task.id}
            className={cn(
              'flex items-center gap-3xl justify-between p-lg rounded-2xl',
              task.disabled ? 'bg-bg-secondary' : 'border border-bg-tertiary'
            )}
          >
            <div className="flex items-center">
              {task.status === MoleculeTaskTableStatus.COMPLETED ? (
                <div className="size-8 flex items-center justify-center bg-green-50 rounded-md mr-4">
                  <Icons.Check className="size-5 text-green-500" />
                </div>
              ) : (
                <div className="size-8 flex items-center justify-center bg-bg-tertiary rounded-md mr-4">
                  <span
                    className={cn(
                      'text-text-primary txt-small-medium',
                      task.disabled
                        ? 'text-text-secondary'
                        : 'text-text-primary'
                    )}
                  >
                    {index + 1}
                  </span>
                </div>
              )}
              <span
                className={cn(
                  `txt-small-medium`,
                  task.status === MoleculeTaskTableStatus.COMPLETED
                    ? 'text-green-600'
                    : task.disabled
                      ? 'text-text-secondary'
                      : 'text-text-primary'
                )}
              >
                {task.name}
              </span>
            </div>
            <div className="flex gap-xl">
              {task.primaryActionButton && (
                <Button
                  onClick={task.primaryActionButton.onClick}
                  disabled={task.disabled}
                  variant={
                    task.status === MoleculeTaskTableStatus.NOT_STARTED
                      ? 'primary'
                      : 'secondary'
                  }
                  size="sm"
                >
                  {task.primaryActionButton.label}
                  {task.primaryActionButton.icon && (
                    <span>{task.primaryActionButton.icon}</span>
                  )}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MoleculeTaskTable;
